import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError } from './http';

/**
 * Server field keys use camelCase + brackets ("contacts[0].fullName", "registeredAddress").
 * react-hook-form paths use dots ("contacts.0.fullName").
 */
export function serverKeyToPath(key: string): string {
  return key.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '');
}

/**
 * Maps `ApiError.fieldErrors` onto a react-hook-form instance.
 * `map` renames server keys to form paths when the form shape differs (e.g. { taxCode: 'legal.taxCode' }).
 * Keys that match no registered field are returned so the caller can show them in a summary.
 * Returns `{ applied, unmatched }`; non-API errors return applied=0.
 */
export function applyFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  opts: { map?: Record<string, string>; knownFields?: readonly string[] } = {},
): { applied: number; unmatched: { field: string; message: string }[] } {
  const unmatched: { field: string; message: string }[] = [];
  if (!(error instanceof ApiError)) return { applied: 0, unmatched };
  let applied = 0;
  let first = true;
  for (const [key, messages] of Object.entries(error.fieldErrors)) {
    const message = messages.join(' ');
    const path = opts.map?.[key] ?? serverKeyToPath(key);
    if (opts.knownFields && !opts.knownFields.some((f) => path === f || path.startsWith(`${f}.`))) {
      unmatched.push({ field: key, message });
      continue;
    }
    setError(path as Path<T>, { type: 'server', message }, { shouldFocus: first });
    first = false;
    applied++;
  }
  return { applied, unmatched };
}

/** Flattens field errors into "field: message" lines (for toasts/summaries). */
export function fieldErrorList(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  return Object.entries(error.fieldErrors).map(([k, v]) => `${k}: ${v.join(' ')}`);
}

/**
 * For useState forms: ApiError.fieldErrors → `{ "contacts.0.fullName": "message" }` (same keys as `validate()` in
 * lib/validation). `map` renames server keys to local keys. Empty object for non-API errors.
 */
export function serverFieldErrors(error: unknown, map: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(error instanceof ApiError)) return out;
  for (const [key, messages] of Object.entries(error.fieldErrors)) {
    out[map[key] ?? serverKeyToPath(key)] = messages.join(' ');
  }
  return out;
}

/** Trims strings and turns empty strings into null — matches the API's optional fields. */
export function emptyToNull<T extends string | null | undefined>(v: T): string | null {
  if (v === undefined || v === null) return null;
  const t = v.trim();
  return t === '' ? null : t;
}
